import React, {useCallback, useMemo, useState} from "react";
import {WorkspaceAggregation} from "@/model/workspace";
import moment from "moment";
import toast from "react-hot-toast";
import {useDialog} from "@/hook/useDialog";
import workspaceRepository from "../../../repository/WorkspaceRepository";
import {RESTORATION_FEATURE_DEVELOPED_AT} from "@/core/variables";
import {useQueryClient} from "@tanstack/react-query";

type WorkspaceDeleteAndRestoreTabProps = {
    workspace: WorkspaceAggregation
};

const WorkspaceDeleteAndRestoreTab: React.FC<WorkspaceDeleteAndRestoreTabProps> = (props) => {
    const { workspace } = props;

    const dialog = useDialog();

    const [loading, setLoading] = useState(false);

    const queryClient = useQueryClient();

    const onMutate = useCallback(async () => {
        try {
            await queryClient.invalidateQueries({
                queryKey: ["workspace", workspace._id],
                exact: true
            })
        } catch (e) {
            toast.error("자동 새로고침에 실패했습니다. 페이지를 새로고침 해주세요.")
        }
    }, [queryClient, workspace._id])

    const isDeletable = useMemo(() => {
        return !loading && !workspace.deleted && !workspace.user?.deleted;
    }, [workspace, loading])

    const isRestorable = useMemo(() => {
        return !loading && workspace.deleted && !workspace.hardDeleted && !workspace.user?.deleted;
    }, [workspace, loading]);

    const isHardDeletable = useMemo(() => {
        return !loading && workspace.deleted && !workspace.hardDeleted;
    }, [workspace, loading])

    const onClickDelete = () => {
        if (workspace.deleted) {
            return;
        }

        dialog.open({
            variant: "danger",
            title: "워크스페이스 삭제",
            content: "워크스페이스을 삭제하시겠습니까? 삭제된 워크스페이스은 30일 이후에 자동으로 영구 삭제됩니다.",
            onConfirm: async () => {
                setLoading(true)

                try {
                    await workspaceRepository.deleteWorkspace(workspace._id);
                    toast.success("워크스페이스을 삭제했습니다.")
                    await onMutate();
                } catch (e) {
                    toast.error("워크스페이스을 삭제하는 도중 에러가 발생했습니다.")
                } finally {
                    setLoading(false)
                }
            }
        })
    }

    const onClickRestore = () => {
        if (!workspace.deleted || workspace.hardDeleted) {
            return;
        }

        if (moment.utc(workspace.deletedAt).isBefore(moment.utc(RESTORATION_FEATURE_DEVELOPED_AT))) {
            toast.error("복구 기능이 개발되기 이전에 삭제된 워크스페이스입니다.")
            return;
        }

        dialog.open({
            variant: "danger",
            title: "삭제된 워크스페이스 복구",
            content: "워크스페이스을 복구하시겠습니까?",
            onConfirm: async () => {
                setLoading(true)

                try {
                    await workspaceRepository.restoreWorkspace(workspace._id);
                    toast.success("워크스페이스을 복구했습니다.")
                    await onMutate();
                } catch (e) {
                    toast.error("워크스페이스을 복구하는 도중 에러가 발생했습니다.")
                } finally {
                    setLoading(false)
                }
            }
        })
    }

    const onClickHardDelete = () => {
        if (!workspace.deleted || workspace.hardDeleted) {
             return;
        }

        if (moment.utc(workspace.deletedAt).isBefore(moment.utc(RESTORATION_FEATURE_DEVELOPED_AT))) {
            toast.error("영구 삭제 기능이 개발되기 이전에 삭제된 채널입니다.")
            return;
        }

        dialog.open({
            variant: "danger",
            title: "워크스페이스 영구 삭제",
            content: "삭제 시점으로부터 30일이 지나면 자동으로 영구 삭제됩니다. 그래도 영구 삭제하시겠습니까?",
            onConfirm: async () => {
                setLoading(true)

                try {
                    await workspaceRepository.hardDeleteWorkspace(workspace._id);
                    toast.success("워크스페이스을 영구 삭제했습니다.")
                    await onMutate();
                } catch (e) {
                    toast.error("워크스페이스을 영구 삭제하는 도중 에러가 발생했습니다.")
                } finally {
                    setLoading(false)
                }
            }
        })
    }

    return (
        <div className="space-y-3">
            <div className="w-full h-[100px] rounded-xl border border-red-400 bg-red-100 p-5 space-y-3">
                <p className="text-red-500">Danger Zone</p>
                <p className="text-sm">고객 요청 혹은 특수 목적이 아니라면 이 탭을 사용하지 마세요. 서비스 중인 채널에 영향을 줄 수 있습니다.</p>
            </div>

            <p>워크스페이스 삭제하기</p>
            <button className="btn btn-danger w-[120px]" disabled={!isDeletable} onClick={onClickDelete}>
                Delete
            </button>

            <p>삭제된 워크스페이스 복구하기 (삭제되지 않았거나 이미 영구 삭제된 워크스페이스 복구할 수 없습니다. 또한, 고객이 탈퇴한 경우에도 복구할 수 없습니다.)</p>
            <button className="btn btn-danger w-[120px]" disabled={!isRestorable} onClick={onClickRestore}>
                Restore
            </button>

            <p>워크스페이스 영구 삭제하기 (삭제되지 않았거나, 이미 영구 삭제된 워크스페이스는 영구 삭제할 수 없습니다.)</p>
            <button className="btn btn-danger w-[120px]" disabled={!isHardDeletable} onClick={onClickHardDelete}>
                Hard Delete
            </button>
        </div>
    );
};

export default WorkspaceDeleteAndRestoreTab;
